import { Box, FormControlLabel, IconButton, MenuItem, Paper, Select, Switch, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined'
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import LinearScaleOutlinedIcon from '@mui/icons-material/LinearScaleOutlined'
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined'
import SortOutlinedIcon from '@mui/icons-material/SortOutlined'
import type { SvgIconComponent } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import FormField from '@/components/researcher/wizard/FormField'
import WizardTextField from '@/components/researcher/wizard/WizardTextField'
import OptionsEditor from './OptionsEditor'
import { kindHasOptions, makeOption, type EditableQuestion, type QuestionEditorConfig, type QuestionKind } from './types'

const KIND_ICON: Record<QuestionKind, SvgIconComponent> = {
  text: TitleOutlinedIcon,
  single: FormatListBulletedOutlinedIcon,
  multi: ChecklistOutlinedIcon,
  scale: LinearScaleOutlinedIcon,
  nps: StarBorderOutlinedIcon,
  rank: SortOutlinedIcon,
}

type QuestionEditorProps = {
  question: EditableQuestion
  index: number
  config: QuestionEditorConfig
  onChange: (patch: Partial<EditableQuestion>) => void
  onDelete: () => void
}

export default function QuestionEditor({ question, index, config, onChange, onDelete }: QuestionEditorProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('questions')

  const changeKind = (kind: QuestionKind) => {
    const needsOptions = kindHasOptions(kind)
    const hadOptions = question.options.length > 0
    onChange({
      kind,
      options: needsOptions
        ? hadOptions
          ? question.options
          : [makeOption(0, !!config.showOptionValues), makeOption(1, !!config.showOptionValues)]
        : [],
    })
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.25, borderRadius: `${theme.affexy.radii.md}px`, borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 1.75 }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            bgcolor: c.gray50,
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            fontSize: 12,
            color: c.ink56,
            fontVariantNumeric: 'tabular-nums',
            flex: '0 0 28px',
          }}
        >
          {index + 1}
        </Box>
        <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 14, flex: 1 }}>
          {t('questionLabel', { number: index + 1 })}
        </Typography>

        <Select
          value={question.kind}
          onChange={(e) => changeKind(e.target.value as QuestionKind)}
          size="small"
          sx={{
            minWidth: 168,
            height: 38,
            borderRadius: '10px',
            bgcolor: '#fff',
            fontSize: 13,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: c.ink08 },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c.ink32 },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.coral },
          }}
          renderValue={(value) => {
            const Icon = KIND_ICON[value as QuestionKind]
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon sx={{ fontSize: 16, color: c.coral }} />
                {t(`kinds.${value}`)}
              </Box>
            )
          }}
        >
          {config.allowedKinds.map((kind) => {
            const Icon = KIND_ICON[kind]
            return (
              <MenuItem key={kind} value={kind} sx={{ fontSize: 13, gap: 1 }}>
                <Icon sx={{ fontSize: 16, color: c.ink56 }} />
                {t(`kinds.${kind}`)}
              </MenuItem>
            )
          })}
        </Select>

        {config.showRequiredToggle && (
          <FormControlLabel
            sx={{ mr: 0 }}
            control={<Switch checked={question.required} onChange={(e) => onChange({ required: e.target.checked })} color="primary" />}
            label={<Typography sx={{ fontSize: 12.5, color: c.ink56 }}>{t('required')}</Typography>}
          />
        )}

        <IconButton aria-label="delete question" onClick={onDelete} sx={{ color: c.danger }}>
          <DeleteOutlineIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      {/* Question text */}
      <WizardTextField value={question.text} onChange={(e) => onChange({ text: e.target.value })} placeholder={t('questionPlaceholder')} />

      {/* Section (templates) */}
      {config.showSection && (
        <FormField label={t('sectionLabel')}>
          <WizardTextField
            value={question.section ?? ''}
            onChange={(e) => onChange({ section: e.target.value })}
            placeholder={t('sectionPlaceholder')}
          />
        </FormField>
      )}

      {/* Options / hints by kind */}
      {kindHasOptions(question.kind) ? (
        <OptionsEditor
          options={question.options}
          onChange={(options) => onChange({ options })}
          showValues={config.showOptionValues}
          marker={question.kind === 'single' ? 'radio' : 'check'}
        />
      ) : question.kind === 'scale' || question.kind === 'nps' ? (
        <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>
          {question.kind === 'scale' ? t('scaleHint') : t('npsHint')}
        </Typography>
      ) : null}
    </Paper>
  )
}
